import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import PermissionMatrix from "../../components/roles/PermissionMatrix";
import RoleSkeleton from "../../components/roles/RoleSkeleton";
import LoadingTransition from "../../components/ui/LoadingTransition";
import {
  Search,
  Plus,
  Edit,
  Trash2,
  ShieldCheck,
  Check,
  X,
  XCircle,
  Users,
} from "lucide-react";
import { useRoles } from "../../hooks/useRoles";
import { useMutation, useQuery } from "@tanstack/react-query";
import RoleManagementForm from "./RoleManagementForm";
import DeleteModal from "../../components/ui/DeleteModal";
import { toast } from "react-toastify";
import UserDropdownModal from "../../components/ui/UserDropdownModal";
import queryClient from "../../lib/queryClient";
import ActionButton from "./ActionButton";

function RoleManagement() {
  const { logout } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isModalLoading, setIsModalLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [userModal, setUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [userModalViewMode, setUserModalViewMode] = useState(false);
  const [permission, setPermission] = useState({});
  const [pageData, setPageData] = useState({
    currentPage: 1,
    totalPages: Math.ceil(roles.length / 10),
    showing: 10,
    total: 0,
  });

  const {
    createRole,
    deleteRole,
    fetchRoles,
    updateRole,
    assignRole,
    removeRole,
  } = useRoles();
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500); // Simulate network delay

    return () => clearTimeout(timer);
  }, []);

  const rolesQuery = useQuery({
    queryKey: ["roles", pageData.currentPage],
    queryFn: fetchRoles,
    keepPreviousData: true,
  });

  useEffect(() => {
    const response = rolesQuery.data;
    setRoles(response?.data?.data || []);
    setPageData(() => ({
      currentPage: response?.data?.pagination?.currentPage || 1,
      totalPages: response?.data?.pagination?.lastPage || 1,
      total: response?.data?.pagination?.total || 0,
      showing: response?.data?.pagination?.showing || 10,
    }));
  }, [rolesQuery.data, pageData.showing, pageData.currentPage]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 800);
  };

  const openModal = (role, mode) => {
    setSelectedRole(role);
    setModalMode(mode);
    setShowModal(true);
    setIsModalLoading(true);
    setTimeout(() => setIsModalLoading(false), 1000);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRole(null);
  };

  const filteredRoles = roles.filter((role) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        role.name.toLowerCase().includes(query) ||
        role.description.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const mutation = {
    createRole: useMutation({
      mutationFn:
        modalMode === "edit"
          ? async (body) => await updateRole(body)
          : async (body) => await createRole(body),
    }),
    deleteRole: useMutation({
      mutationFn: async (id) => await deleteRole(id),
    }),
    assignRole: useMutation({
      mutationFn: async (selectedUser) => await assignRole(selectedUser),
    }),
    removeRole: useMutation({
      mutationFn: async (id) => await removeRole(id),
    }),
  };

  const handleDelete = {
    onDelete: (role) =>
      mutation.deleteRole.mutate(role._id, {
        onSuccess: async (data) => {
          toast.success(data.message || "Role Deleted");
          setDeleteModal(false);
        },
      }),
    onClose: () => setDeleteModal(false),
    onOpen: (role) => {
      setDeleteModal(true);
      setSelectedRole(role);
    },
  };

  const handleSubmit = (data) => {
    mutation.createRole.mutate(data, {
      onSuccess: async (data) => {
        toast.success(data.message || "Role Created");
        closeModal();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });
  };

  const handleAssignRole = () => {
    mutation.assignRole.mutate(
      { user_id: selectedUser?._id || "", role_id: selectedRole?._id || "" },
      {
        onSuccess: async (data) => {
          await queryClient.invalidateQueries(["roles"]);
          toast.success(
            data?.data?.message || `User assigned to role successfully`
          );
          setSelectedUser({});
          setUserModal(false);
        },
        onError: (error) => {
          console.error(error, "<---ERROR");
          toast.error(error.message);
        },
      }
    );
  };

  const handleRemoveRole = (id) => {
    mutation.removeRole.mutate(id, {
      onSuccess: async (data) => {
        toast.success("Role removed");
        await queryClient.invalidateQueries(["roles"]);
        setUserModalViewMode(false);
      },
      onError: (error) => toast.error(error.message),
    });
  };

  return (
    <div>
      <main className="">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold">Role Management</h1>
          {permission.add && (
            <button
              onClick={() => openModal(null, "create")}
              className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create New Role
            </button>
          )}
        </div>

        {/* Use LoadingTransition to smooth the transition between loading and loaded states */}
        <LoadingTransition
          isLoading={isLoading}
          skeleton={<RoleSkeleton />}
          transition={{ duration: 0.3 }}
        >
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search roles..."
                  value={searchQuery}
                  onChange={handleSearch}
                  className="pl-10 pr-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Role Name
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Description
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Permissions
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Assign User
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Users Assigned
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRoles.length > 0 ? (
                    filteredRoles.map((role) => (
                      <tr key={role._id} className="hover:bg-gray-50 border-b">
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-primary-50 rounded-full flex items-center justify-center">
                              <ShieldCheck className="h-4 w-4 text-primary" />
                            </div>
                            <div className="ml-3">
                              <div className="font-medium text-gray-900">
                                {role.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {role.description}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex flex-wrap gap-1 max-w-32">
                            {role.permissions
                              .slice(0, 3)
                              .map((permission, index) => (
                                <span
                                  key={index}
                                  className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full"
                                >
                                  {permission?.module_name}
                                </span>
                              ))}
                            {role.permissions.length > 3 && (
                              <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full">
                                +{role.permissions.length - 3} more
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center">
                            <button
                              className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                              onClick={() => {
                                setUserModal(true);
                                setSelectedRole(role);
                              }}
                            >
                              Assign
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div
                            className="flex items-center cursor-pointer"
                            onClick={() => {
                              setUserModalViewMode(true);
                              setSelectedRole(role);
                              setUserModal(true);
                            }}
                          >
                            <Users className="h-4 w-4 text-gray-400 mr-1" />
                            <span>{role.users_count}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <ActionButton
                            item={role}
                            openModal={openModal}
                            key={role._id}
                            handleDelete={handleDelete}
                            setPermission={setPermission}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No roles found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </LoadingTransition>
      </main>
      <DeleteModal
        isOpen={deleteModal}
        onClose={handleDelete.onClose}
        onDelete={() => handleDelete.onDelete(selectedRole)}
        title="User"
      />
      <UserDropdownModal
        isOpen={userModal}
        onClose={() => {
          setUserModal(false);
          setTimeout(() => {
            setUserModalViewMode(false);
          }, 500);
        }}
        onSelect={(data) => {
          if(data._id === selectedUser?._id) {
            setSelectedUser({});
            return;
          }
          else{
            setSelectedUser(data);
          }
        }}
        selectedUserId={selectedUser?._id || ""}
        handleAssignUserToRole={handleAssignRole}
        assignedUsers={selectedRole?.users || []}
        viewMode={userModalViewMode}
        removeRole={(user_id) => handleRemoveRole(user_id)}
      />
      {/* Role Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeModal}
          ></div>
          <div className="relative bg-white rounded-lg w-full max-w-3xl h-[90vh] mx-4 shadow-xl flex flex-col overflow-auto p-4">
            {/* Modal Header */}
            <div className="px-2 py-2 border-b relative shrink-0">
              <h3 className="text-lg font-medium">
                {modalMode === "view"
                  ? "Role Details"
                  : modalMode === "edit"
                  ? "Edit Role"
                  : "Create Role"}
              </h3>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                onClick={closeModal}
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <RoleManagementForm
              mode={modalMode}
              onCancel={closeModal}
              onSubmit={(data) => handleSubmit(data)}
              isModalLoading={isModalLoading}
              defaultValues={selectedRole}
              submissionLoading={""}
            />

            {/* Modal Footer */}
          </div>
        </div>
      )}
    </div>
  );
}

export default RoleManagement;
